/**
 * Purpose: Powers the "You May Also Like" recommendation carousel in the
 *          Space Dinosaurs cart drawer - shows/hides the previous/next arrow
 *          buttons based on scroll overflow and pages the card list by two
 *          card widths (one pair) per click.
 *
 * Public API: <cart-drawer-recommendations-component> custom element.
 *             No public methods intended for external callers; on:click
 *             wiring (scrollPrevious/scrollNext) is internal to its own
 *             markup in snippets/sd-cart-drawer.liquid.
 *
 * Upstream deps: @theme/component (Component base class), @theme/scrolling
 *                (Scroller helper, prefers-reduced-motion aware).
 *
 * Downstream consumers: snippets/sd-cart-drawer.liquid (cart drawer +
 *                        empty-cart drawer state). Lives inside the
 *                        `data-hydration-key="cart-drawer-inner"` subtree, so
 *                        it is morphed (not reconnected) on cart mutations.
 *
 * Failure modes: no-ops safely when the list/button refs are missing (e.g.
 *                zero recommendations rendered). Arrow visibility/disabled
 *                state is recomputed on resize, on scroll end (both user
 *                drags and programmatic button clicks, since scrolling.js
 *                fires onScrollEnd unconditionally), and after every
 *                cart-update morph via updatedCallback. Safe to call
 *                repeatedly, no side effects beyond DOM attribute updates.
 *                Both arrow disabled thresholds are read from the list's
 *                computed scroll-padding (not assumed to be 0) plus a 1px
 *                epsilon: the previous arrow uses
 *                `scroll-padding-inline-start` and the next arrow uses
 *                `scroll-padding-inline-end`, since the snap rest position
 *                at either end sits at that padding offset - not scrollLeft
 *                0 or scrollWidth - clientWidth exactly - and engines can
 *                differ by a sub-pixel when settling there.
 *
 * Performance: standard - one ResizeObserver and one scroll listener per
 *              drawer instance, values recomputed only on resize/scroll end/
 *              morph, not on every scroll frame.
 */
import { Component } from '@theme/component';
import { Scroller } from '@theme/scrolling';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} list - The scrollable list of recommendation cards.
 * @property {HTMLButtonElement} [previousButton] - The "previous" arrow button.
 * @property {HTMLButtonElement} [nextButton] - The "next" arrow button.
 * @property {HTMLAnchorElement[]} [cards] - The recommendation card links.
 *
 * @extends {Component<Refs>}
 */
class CartDrawerRecommendations extends Component {
  requiredRefs = ['list'];

  /** @type {Scroller | null} */
  #scroller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /**
   * The list element the current Scroller/ResizeObserver are attached to, so
   * updatedCallback can detect when a cart-update morph replaces it.
   * @type {HTMLElement | null}
   */
  #observedList = null;

  connectedCallback() {
    super.connectedCallback();
    this.#attach();
  }

  /**
   * Called after a cart-update morph patches this component's subtree
   * (data-hydration-key="cart-drawer-inner" morphs call updatedCallback
   * instead of reconnecting). Re-attaches the Scroller/ResizeObserver if the
   * morph replaced the list node, and always recomputes arrow state since
   * the morph can change which/how many cards are rendered (e.g. a
   * recommended product that just got added to the cart is filtered out).
   */
  updatedCallback() {
    super.updatedCallback();
    this.#attach();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detach();
  }

  /**
   * Attaches the Scroller/ResizeObserver to the current list ref if needed,
   * then recomputes arrow visibility/disabled state.
   */
  #attach() {
    const { list } = this.refs;
    if (!list) return;

    if (list !== this.#observedList) {
      this.#detach();

      this.#scroller = new Scroller(list, {
        onScroll: this.#updateControls,
        onScrollEnd: this.#updateControls,
      });

      this.#resizeObserver = new ResizeObserver(this.#updateControls);
      this.#resizeObserver.observe(list);

      this.#observedList = list;
    }

    this.#updateControls();
  }

  /**
   * Disconnects the current Scroller/ResizeObserver, if any.
   */
  #detach() {
    this.#scroller?.destroy();
    this.#scroller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#observedList = null;
  }

  /**
   * Scrolls the recommendation list back one pair (two cards) to the left.
   */
  scrollPrevious() {
    this.#scrollByCard(-1);
  }

  /**
   * Scrolls the recommendation list forward one pair (two cards) to the
   * right.
   */
  scrollNext() {
    this.#scrollByCard(1);
  }

  /**
   * Scrolls the list by the width of one pair (two cards plus the gap
   * between them), in the given direction, so each arrow click pages
   * cleanly to the next/previous pair's snap point.
   * @param {1 | -1} direction
   */
  #scrollByCard(direction) {
    const { list, cards } = this.refs;
    if (!list || !this.#scroller) return;

    const card = cards?.[0];
    if (!card) return;

    const step = (card.getBoundingClientRect().width + this.#gap) * 2;
    this.#scroller.by(step * direction);
  }

  /**
   * Gap (in px) between cards, read from the list's computed `column-gap`.
   * @type {number}
   */
  get #gap() {
    const { list } = this.refs;
    if (!list) return 0;

    return parseFloat(getComputedStyle(list).columnGap) || 0;
  }

  /**
   * The list's inline-start scroll-padding (in px), read from computed
   * style rather than hardcoded. The list has `scroll-padding-inline`
   * matching its `padding-inline` so the snap engine treats the padded
   * edge as the snap rest position, but engines are not required to
   * settle scrollLeft at exactly 0 for that rest position (Chromium does;
   * Safari's snap rounding can leave a sub-pixel remainder) - this value
   * is combined with a small epsilon below rather than trusted exactly.
   * @type {number}
   */
  get #scrollPaddingStart() {
    const { list } = this.refs;
    if (!list) return 0;

    return parseFloat(getComputedStyle(list).scrollPaddingInlineStart) || 0;
  }

  /**
   * The list's inline-end scroll-padding (in px), read from computed style
   * rather than hardcoded. Mirrors #scrollPaddingStart: the snap rest
   * position at the end of the list settles at
   * `scrollWidth - clientWidth - scrollPaddingInlineEnd`, not exactly
   * `scrollWidth - clientWidth`, so this value is combined with a small
   * epsilon below rather than trusted exactly.
   * @type {number}
   */
  get #scrollPaddingEnd() {
    const { list } = this.refs;
    if (!list) return 0;

    return parseFloat(getComputedStyle(list).scrollPaddingInlineEnd) || 0;
  }

  /**
   * Shows/hides the arrows entirely when there is nothing to scroll, and
   * disables whichever arrow points past the current scroll bounds.
   */
  #updateControls = () => {
    const { list, previousButton, nextButton } = this.refs;
    if (!list) return;

    const hasOverflow = list.scrollWidth > list.clientWidth + 1;

    if (previousButton) {
      previousButton.hidden = !hasOverflow;
      previousButton.disabled = list.scrollLeft <= this.#scrollPaddingStart + 1;
    }

    if (nextButton) {
      nextButton.hidden = !hasOverflow;
      const maxScrollLeft = list.scrollWidth - list.clientWidth;
      nextButton.disabled = list.scrollLeft >= maxScrollLeft - this.#scrollPaddingEnd - 1;
    }
  };
}

if (!customElements.get('cart-drawer-recommendations-component')) {
  customElements.define('cart-drawer-recommendations-component', CartDrawerRecommendations);
}
